import { useEffect, useState } from 'react';
import { Admin } from '../../api.js';
import { FormField, Switch } from '../../components/FormField.jsx';
import { PageHeader } from '../../components/PageHeader.jsx';
import { Modal, ConfirmDialog, submitForm } from '../../components/Modal.jsx';

const empty = { name: '', tier: '', description: '', active: true };

export default function Teams() {
  const [teams, setTeams] = useState([]);
  const [modal, setModal] = useState(null); // null | 'create' | { id, ... }
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    const r = await Admin.teams.list();
    setTeams(r.teams);
  }
  useEffect(() => {
    refresh();
  }, []);

  function openCreate() {
    setForm(empty);
    setModal('create');
  }
  function openEdit(t) {
    setForm({ name: t.name, tier: t.tier || '', description: t.description || '', active: !!t.active });
    setModal({ id: t.id });
  }
  async function save(e) {
    e.preventDefault();
    setBusy(true);
    try {
      if (modal === 'create') await Admin.teams.create(form);
      else await Admin.teams.update(modal.id, form);
      setModal(null);
      await refresh();
    } finally {
      setBusy(false);
    }
  }
  async function remove(id) {
    await Admin.teams.remove(id);
    await refresh();
  }

  return (
    <div>
      <PageHeader
        eyebrow="Squads"
        title="Teams"
        description="A side, B side, Colts, Women's, Vets — whatever you field."
        action={<button onClick={openCreate} className="btn-primary btn-sm">+ New team</button>}
      />
      {teams.length === 0 ? (
        <EmptyState onAction={openCreate} label="Create a team" />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {teams.map((t) => (
            <div key={t.id} className={`card flex flex-col ${!t.active ? 'opacity-60' : ''}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-display text-xl">{t.name}</h3>
                  {t.tier && <div className="eyebrow text-brand mt-1">{t.tier}</div>}
                </div>
                {t.active ? <span className="chip-success chip-dot">Active</span> : <span className="chip-neutral">Inactive</span>}
              </div>
              {t.description && <p className="text-sm text-ink-2 mt-3 line-clamp-3">{t.description}</p>}
              <div className="mt-auto pt-5 flex gap-2">
                <button className="btn-outline btn-xs" onClick={() => openEdit(t)}>Edit</button>
                <button className="btn-ghost btn-xs text-danger" onClick={() => setConfirm(t)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={!!modal}
        onClose={() => setModal(null)}
        eyebrow={modal === 'create' ? 'New' : 'Edit'}
        title={modal === 'create' ? 'Create a team' : 'Edit team'}
        footer={
          <>
            <button onClick={() => setModal(null)} className="btn-ghost">Cancel</button>
            <button type="button" onClick={() => submitForm('team-form')} className="btn-primary" disabled={busy}>
              {busy ? 'Saving…' : modal === 'create' ? 'Create' : 'Save'}
            </button>
          </>
        }
      >
        <form id="team-form" onSubmit={save} className="space-y-4">
          <FormField label="Name" required>
            <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus />
          </FormField>
          <FormField label="Tier" hint="Short label like A, B, Colts, Women's">
            <input className="input" value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} />
          </FormField>
          <FormField label="Description">
            <textarea className="input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </FormField>
          <Switch checked={form.active} onChange={(v) => setForm({ ...form, active: v })} label="Active" hint="Inactive teams are hidden from players" />
        </form>
      </Modal>

      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => remove(confirm.id)}
        title={`Delete ${confirm?.name}?`}
        message="Players on this team will keep their records but the team is removed."
        confirmLabel="Delete"
        danger
      />
    </div>
  );
}

export function EmptyState({ onAction, label, hint = 'Nothing here yet.' }) {
  return (
    <div className="card-flush py-16 text-center">
      <div className="font-display text-ink-3 text-2xl italic mb-4">{hint}</div>
      <button onClick={onAction} className="btn-primary">{label}</button>
    </div>
  );
}
